import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import List from "components/list";
import HeaderPortal from "components/portal/header-portal";
import { eventBus } from "utils/eventBus";
import ProjectNameCell from "@/modules/work/projects/cells/name";
import { NameCell } from "@/components/cells/name";
import { DateTimeCell } from "components/cell/date/cell";
import { EmployeeCell } from "components/cell/employee/cell";
import { getEmployeeFields } from "components/user/config";
import { StatusCell } from "@/components/status/cell";
import { syncRepositories } from "./repo/api";
import SyncButton from "./sync-button";
import RepoLabel from "./repo-label";
import GithubRepoFilters, { DEFAULT_FILTERS } from "./filters";

const ENDPOINT = "git_repositories";
const repoProjectSearch = (search) => ({ project: { name: { _icontains: search.trim() } } });

export default function GithubIntegrations({ storageKey = "integrations-github", title = "Repositories" }) {
  const sync = useMutation({
    mutationFn: syncRepositories,
    onSuccess: () => {
      toast.success("Repositories synced");
      eventBus.emit("refresh:list", `${ENDPOINT}${storageKey}`);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to sync repositories");
    },
  });

  const columns = [
    {
      id: "project",
      header: "Project",
      accessorKey: "project",
      // Repositories without a project show nothing instead of the placeholder logo.
      cell: ({ row }) =>
        row.original?.project?.id ? <ProjectNameCell row={{ original: row.original.project }} /> : null,
    },
    {
      id: "pc",
      header: "PC",
      accessorFn: (row) => row?.project?.coordinator,
      cell: EmployeeCell,
    },
    {
      id: "project_status",
      header: "Status",
      accessorFn: (row) => row?.project?.status,
      cell: StatusCell,
    },
    {
      id: "repository",
      header: "Repository",
      accessorFn: (row) => row?.repository_url?.split("/").slice(-2).join("/") || "-",
      // Repo name followed by the GitHub-style visibility / archive pill.
      cell: (info) => (
        <div className="flex items-center gap-2 min-w-0">
          <span className="hover:underline truncate">
            <NameCell {...info} />
          </span>
          <RepoLabel visibility={info.row.original?.visibility} flag={info.row.original?.flag} />
        </div>
      ),
      href: (row) => row?.repository_url || false,
      linkProps: { target: "_blank", rel: "noreferrer" },
    },
    {
      id: "branch_count",
      header: "Branches",
      accessorKey: "branch_count",
      sortable: true,
    },
    {
      id: "employee_count",
      header: "Employees",
      accessorKey: "employee_count",
      sortable: true,
    },
    {
      id: "date_synced",
      header: "Last Synced",
      accessorKey: "date_synced",
      cell: DateTimeCell,
    },
  ];

  const fields = [
    "id",
    "project.id",
    "project.name",
    "project.logo",
    "project.project_id",
    "project.status",
    ...getEmployeeFields().map((field) => `project.coordinator.${field}`),
    "repository_url",
    "suffix",
    "flag",
    "visibility",
    "branch_count",
    "employee_count",
    "date_synced",
  ];

  return (
    <div className="w-full">
      {/* Page header action, like the create button on other lists. Sync confirms first. */}
      <HeaderPortal>
        <SyncButton
          onSync={() => sync.mutateAsync()}
          isSyncing={sync.isPending}
          title="Sync all repositories?"
          description="Pulls every repository of the GitHub organization and refreshes branch and employee counts."
        />
      </HeaderPortal>
      <div className="flex-1 h-full">
        <List
          title={title}
          endPoint={ENDPOINT}
          sortBy="id"
          storageKey={storageKey}
          columns={columns}
          filters={DEFAULT_FILTERS}
          renderFilters={GithubRepoFilters}
          meta={{ fields, customSearch: repoProjectSearch }}
          searchPlaceholder="Search project"
          href={(row) => row?.id && `/integrations/github/repositories/${row.id}`}
        />
      </div>
    </div>
  );
}
